/** Shared, side-effect-free business findings. Authorization and operational
 * errors stay at their existing boundaries; this is not a rule registry. */
import type { z } from "zod";

export type EntityType = "contact" | "lead" | "booking" | "tour" | "calendar_block"
  | "payment" | "commission" | "commission_rule" | "venue" | "catalog"
  | "settings" | "member" | "event_type" | "lead_source" | "note" | "message"
  | "api_key" | "contract" | "attachment" | "booking_line" | "staff_trip"
  | "contract_signer" | "contract_event" | "contract_template"
  | "payment_request" | "card_hold"
  // Phase 5 automation
  | "booking_vendor" | "message_template" | "drip_sequence" | "drip_enrollment"
  // Phase 6 vendor directory
  | "vendor" | "vendor_category" | "vendor_venue_pref";
export type ValidationAction = `${EntityType}.save` | "booking.reserve" | "booking.move"
  | "lead.convert" | "lead.transition" | "tour.schedule" | "calendar_block.reserve"
  | "payment.record" | "payment.refund" | "commission.pay" | "contract.send"
  | "contract.sign" | "contract.countersign" | "contract.remind" | "contract.void" | "booking.review" | "booking.approve" | "booking.needs_review"
  | "attachment.delete" | "comms.send_email" | "comms.send_sms" | "audit"
  | "payment_request.create" | "payment_request.cancel" | "payment.checkout"
  | "card_hold.capture" | "card_hold.release"
  | "booking.insurance" | "booking.insurance_waive"
  | "message.release" | "message.cancel"
  | "vendor.deactivate" | "vendor.attach" | "vendor.coi";
export interface EntityRef { type: EntityType; id?: string }
export interface ValidationIssue {
  code: string;
  kind: "missing" | "warning" | "error";
  entity: EntityRef;
  fields: string[];
  message: string;
  blocks: ValidationAction[];
  /** Only caller-authorized records may be included. */
  related?: EntityRef[];
  /** Pair identity is independent of which record displays the finding. */
  pair?: [EntityRef, EntityRef];
  resource?: string;
}
export interface ValidationEvaluation {
  issues: ValidationIssue[];
  checksComplete: boolean;
  /** Injected by the server; pure local evaluations need not supply it. */
  checkedAt?: string;
  /** Earliest applicable authorized hold expiry; a read-only refresh hint. */
  nextExpiry?: string;
}

const refKey = (ref: EntityRef) => [ref.type, ref.id ?? "draft"];
export function issueKey(issue: ValidationIssue): string {
  return JSON.stringify(issue.pair
    ? [issue.code, issue.pair.map(refKey).sort((a, b) => {
      const left = JSON.stringify(a), right = JSON.stringify(b);
      return left < right ? -1 : left > right ? 1 : 0;
    }), issue.resource ?? ""]
    : [issue.code, refKey(issue.entity), [...new Set(issue.fields)].sort()]);
}
export function uniqueIssues(issues: readonly ValidationIssue[]): ValidationIssue[] {
  const found = new Map<string, ValidationIssue>();
  for (const issue of issues) {
    const key = issueKey(issue);
    const previous = found.get(key);
    // The same canonical finding can be needed by more than one action.
    found.set(key, previous ? { ...previous, blocks: [...new Set([...previous.blocks, ...issue.blocks])] } : issue);
  }
  return [...found.values()];
}
export function evaluation(issues: readonly ValidationIssue[], checksComplete: boolean, checkedAt?: string): ValidationEvaluation {
  return { issues: uniqueIssues(issues), checksComplete, ...(checkedAt === undefined ? {} : { checkedAt }) };
}
export function blockingIssues(result: ValidationEvaluation, action: ValidationAction): ValidationIssue[] {
  return result.issues.filter(issue => issue.blocks.includes(action));
}
/** Incomplete required checks can never authorize a mutation. Callers evaluate
 * only the checks applicable to their action (not unrelated future actions). */
export function actionAllowed(result: ValidationEvaluation, action: ValidationAction): boolean {
  return result.checksComplete && blockingIssues(result, action).length === 0;
}
export function zodIssues(errors: readonly z.core.$ZodIssue[], entity: EntityRef, action: ValidationAction, prefix = ""): ValidationIssue[] {
  return errors.map(error => {
    const path = [prefix, ...error.path.map(String)].filter(Boolean).join(".");
    return { code: `${entity.type}.input_invalid`, kind: "error", entity, fields: path ? [path] : [], message: error.message, blocks: [action] };
  });
}

/** Parse explicit command input, not persisted audit rows. Keeping errors in
 * the same format protects direct service callers as well as HTTP handlers. */
export function parseInput<S extends z.ZodType>(schema: S, input: unknown, entity: EntityRef, action: ValidationAction):
  { success: true; data: z.output<S>; issues: [] } | { success: false; issues: ValidationIssue[] } {
  const parsed = schema.safeParse(input);
  return parsed.success ? { success: true, data: parsed.data, issues: [] } : { success: false, issues: zodIssues(parsed.error.issues, entity, action) };
}
